import { Component, HostBinding, ViewChild, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import * as _ from 'underscore';
import { isArray, isNullOrUndefined } from '../../../../models/utils';
import { DrNamedQuery } from '../../../../models/dr-named-query';
import { AnalyticalSettings, EmailThreadingModes, AnalyticalSettingsViewModel } from '../../../../models/analytical-settings';
import { Endpoint, HelpIndex, SettingsType } from '../../../../models/enums';
import { ActionType, ObjectActionListType, Permissions } from '../../../../models/permission';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { Template } from '../../../../models/template';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { KeyValueListComponent } from '../../../key-value-list/key-value-list.component';

@Component({
    standalone: false,
    selector: 'res-analytic-settings',
    templateUrl: './analytic-settings.component.html',
    styleUrls: ['./analytic-settings.component.scss']
})
export class AnalyticSettingsComponent implements SettingsNavigationContentComponent, OnInit {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild('contentForm', { static: true }) contentForm: NgForm;
    @ViewChild(KeyValueListComponent, { static: true }) caseDiscardCandidatesNamedQueries: KeyValueListComponent;

    isBusy = false;
    isBusyMessage: string;
    helpIndex: HelpIndex;
    handle: string;
    settings: AnalyticalSettings;
    viewModel: AnalyticalSettingsViewModel;
    emailThreadingModes = {
        [EmailThreadingModes.RFC_2822_METADATA]: false,
        [EmailThreadingModes.MS_OTLOOK_METADATA]: false,
        [EmailThreadingModes.CONTENT_BASED]: false,
        [EmailThreadingModes.SMF_CHAT_ID_BASED]: false
    };

    modes = this.commonService.getEnumNames(EmailThreadingModes);
    originalEmailThreadingModes = {};

    hasPermissions = true;
    validChanges = true;
    isSystemUser = false;

    settingsType: SettingsType;

    get isDirty(): boolean {
        if (this.contentForm) {
            return this.contentForm.dirty || this.caseDiscardCandidatesNamedQueries.keyValueForm.form.dirty ||
                this.viewModel.isDirty();
        }
        return false;
    }
    set isDirty(value: boolean) {
        if (this.contentForm && this.contentForm.control) {
            if (value) {
                this.contentForm.control.markAsDirty();
            } else {
                this.contentForm.control.markAsPristine();
            }
        }
    }

    constructor(private commonService: CommonService, private restService: RestService) { }

    ngOnInit() {
        this.viewModel = new AnalyticalSettingsViewModel(new AnalyticalSettings()); // to get page to load. Will overwrite when load.
    }

    selectedTreeItemChanged(node: GenericTreeNode, projectHandle?: string, settingsType?: SettingsType): void {
        this.settingsType = settingsType;
        this.modes.forEach(element => {
            this.emailThreadingModes[element] = this.originalEmailThreadingModes[element] = false;
        });
        this.handle = node.data && node.data instanceof Template ? node.data.handle :
            projectHandle ? projectHandle : this.commonService.currentOrganizationHandle;
        this.helpIndex = projectHandle ? HelpIndex.myedisc_case_analytic_settings_dlg : (settingsType === SettingsType.system ? HelpIndex.sys_template_analytic_dlg : HelpIndex.org_analytic_settings_template_dlg);
        this.isSystemUser = (settingsType === SettingsType.system && (this.commonService.currentUser?.originalCustomerName === 'super_system_customer' || this.commonService.currentUser.importedFromSystemUser));
        const permissionType = projectHandle ? ObjectActionListType.PRJ_ANALYTIC_SETTINGS : this.isSystemUser ? ObjectActionListType.SYSTEM_ANALYTIC_SETTINGS : ObjectActionListType.ORG_ANALYTIC_SETTINGS;
        this.hasPermissions = Permissions.checkUserPermissions([{ permissionType: permissionType, permissionActions: [ActionType.CREATE] }]);
        this.loadSettingsData();
    }

    loadSettingsData(): void {
        if (this.handle) {
            this.isBusyMessage = 'Loading Analytic Settings...';
            this.isBusy = true;

            this.restService.post(Endpoint.projectManager_getAnalyticalSettings, { handle: this.handle, systemScope: this.isSystemUser }).then(response => {
                if (response && response.analyticalSettings && response.analyticalSettings.handle === this.handle) {
                    this.viewModel = new AnalyticalSettingsViewModel(new AnalyticalSettings(response.analyticalSettings));
                    if (this.commonService.currentProject) {
                        this.commonService.currentProject.deDupScope = this.viewModel.model.deDupScope;
                    }
                    this.modes.forEach(element => {
                        this.emailThreadingModes[element] = this.originalEmailThreadingModes[element] = false;
                    });
                    if (isArray(this.viewModel.model.emailThreadingModes)) {
                        this.viewModel.model.emailThreadingModes.forEach(element => {
                            this.emailThreadingModes[element] = this.originalEmailThreadingModes[element] = true;
                        });
                    }
                    this.viewModel.model.caseDiscardCandidatesNamedQueries = _.sortBy(this.viewModel.model.caseDiscardCandidatesNamedQueries, 'name');
                    const emptyNamedQuery = new DrNamedQuery({ name: '', query: '', description: null, annotationText: null });
                    this.viewModel.model.caseDiscardCandidatesNamedQueries.push(new DrNamedQuery(emptyNamedQuery));
                }
                this.isBusy = false;
                this.isDirty = false;
            }, error => {
                this.isBusy = false;
            });
        }
    }

    saveChanges(fromAutoSave = false): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            this.isBusy = true;
            this.isBusyMessage = 'Validating Queries ...';

            this.caseDiscardCandidatesNamedQueries.validateQueries(this.caseDiscardCandidatesNamedQueries, this.viewModel.model.caseDiscardCandidatesNamedQueries).then(isQueriesValid => {
                if (!isQueriesValid) {
                    // If queries validation fails, resolve with false and return without saving the analytic settings
                    this.isBusy = false;
                    resolve(false);
                    return;
                }

                if (this.handle) {
                    const modes = [];
                    for (const key in this.emailThreadingModes) {
                        if (Object.prototype.hasOwnProperty.call(this.emailThreadingModes, key)) {
                            if (this.emailThreadingModes[key] === true) {
                                modes.push(key);
                            }
                        }
                    }

                    const analyticalSettings = new AnalyticalSettings(this.viewModel.model);
                    analyticalSettings.emailThreadingModes = modes;
                    analyticalSettings.caseDiscardCandidatesNamedQueries = DrNamedQuery.removeEmptyQueries(this.viewModel.model.caseDiscardCandidatesNamedQueries);

                    this.isBusyMessage = 'Saving Analytical Settings...';
                    this.isBusy = true;
                    this.restService.post(Endpoint.projectManager_setAnalyticalSettings, { handle: this.handle, analyticalSettings: analyticalSettings, systemScope: this.isSystemUser }).then(response => {
                        this.isDirty = false;
                        this.isBusy = false;
                        this.resetState();
                        if (!fromAutoSave) {
                            this.loadSettingsData();
                        }
                        resolve(true);
                    }, error => {
                        this.isBusy = false;
                        resolve(false);
                    });
                } else {
                    this.isBusy = false;
                    resolve(false);
                }
            }).catch(error => {
                this.isBusy = false;
                reject(error);
            })
        });
    }

    resetState(): void {
        if (!isNullOrUndefined(this.caseDiscardCandidatesNamedQueries)) {
            this.caseDiscardCandidatesNamedQueries.resetState();
        }
    }

    discardChanges(): void {
        this.isDirty = false;
        this.resetState();
        this.loadSettingsData();
    }

    isSaveDisabled(): boolean {
        let isEnabled = false;
        const hasErrors = this.caseDiscardCandidatesNamedQueries.hasErrors();
        if (!this.isBusy && !hasErrors) {
            if (this.caseDiscardCandidatesNamedQueries.keyValueForm.form.dirty) {
                isEnabled = true;
            } else if (!this.caseDiscardCandidatesNamedQueries.keyValueForm.form.dirty && this.isDirty) {
                isEnabled = true;
            }
        }
        return !isEnabled;
    }

    isDiscardDisabled(): boolean {
        let isEnabled = false;
        if (!this.isBusy) {
            if (this.caseDiscardCandidatesNamedQueries.keyValueForm.form.dirty || this.isDirty) {
                isEnabled = true;
            }
        }
        return !isEnabled;
    }

    onChange() {
        this.isDirty = this.viewModel.isDirty();
        for (let i = 0; i < this.modes.length && !this.isDirty; i++) {
            if (this.emailThreadingModes[this.modes[i]] !== this.originalEmailThreadingModes[this.modes[i]]) {
                this.isDirty = true;
                break;
            }
        }
    }

    openHelp = () => this.commonService.openHelp(this.helpIndex);
}
